import { Children } from 'react'
import clsx from 'clsx'
import { DefaultSkeletonRow } from './DefaultSkeletonRow'
import { TableHeaderCell } from './TableHeaderCell'
import { NoDataRow } from './NoDataRow'
import { ProgressRow } from './ProgressRow'

export const AccountTable = ({
  children,
  headers = [],
  isLoading = false,
  isFetching = false,
  skeletonRows = 5,
  skeletonColumns = 4,
  NoDataRowComponent = NoDataRow,
  SkeletonRowComponent = DefaultSkeletonRow,
  tableNamespace = 'table',
  className = '',
  theadClassName = '',
  stickyHeader = false,
  searchCriteria,
  ...props
}: AccountTableProps) => {
  const hasData = children && Children.count(children) > 0

  // Show skeleton only on initial load (before first data arrives)
  const showSkeleton = isLoading && !hasData

  return (
    <div className="overflow-x-auto overflow-y-hidden ring-1 ring-gray-200 md:rounded-md">
      <table
        className={`min-w-full divide-y divide-gray-300 text-base text-gray-900 ${className}`}
        {...props}
      >
        {headers.length > 0 && (
          <thead
            className={clsx(
              'bg-gray-50',
              stickyHeader ? 'sticky top-0 z-10 shadow-xs' : 'relative',
              theadClassName
            )}
          >
            <tr>
              {headers.map((header, index) => (
                <TableHeaderCell
                  key={header.key || index}
                  sortKey={header.sortKey}
                  sortable={header.sortable}
                  tableNamespace={tableNamespace}
                  align={header.align}
                  className={header.className}
                >
                  {header.label}
                </TableHeaderCell>
              ))}
            </tr>
            <ProgressRow
              numOfColumns={headers.length}
              isFetching={isFetching}
            />
          </thead>
        )}
        <tbody className="divide-y divide-gray-200 bg-white">
          {/*<ProgressRow numOfColumns={headers.length} isFetching={isFetching} />*/}
          {showSkeleton ? (
            // Show skeleton rows during initial loading
            Array.from({ length: skeletonRows }).map((_, index) => (
              <SkeletonRowComponent
                key={`skeleton-${index}`}
                columns={headers.length || skeletonColumns}
              />
            ))
          ) : children && Children.count(children) > 0 ? (
            children
          ) : (
            <NoDataRowComponent searchCriteria={searchCriteria} />
          )}
        </tbody>
      </table>
    </div>
  )
}

interface AccountTableProps {
  children: React.ReactNode
  headers: Array<{
    label: React.ReactNode
    sortable?: boolean
    sortDirection?: 'asc' | 'desc' | null
    sortKey?: string
    className?: string
    key?: string
    align?: 'left' | 'right'
    onClick?: () => void
  }>
  isLoading?: boolean
  isFetching?: boolean
  skeletonRows?: number
  skeletonColumns?: number
  SkeletonRowComponent?: React.ComponentType<{ columns: number }>
  NoDataRowComponent?: React.ComponentType<{ searchCriteria?: string | null }>
  tableNamespace?: string
  searchCriteria?: string | null
  className?: string
  theadClassName?: string
  stickyHeader?: boolean
  props?: React.HTMLAttributes<HTMLTableElement>
}
